import React, { useMemo, useState } from 'react';
import { DocumentSchemaRecord } from '../../../services/documentSchemaService';
import { ArrowLeftRight } from 'lucide-react';

type DiffLineType = 'same' | 'added' | 'removed';

interface DiffLine {
    type: DiffLineType;
    oldNum?: number;
    newNum?: number;
    text: string;
}

const computeDiff = (oldLines: string[], newLines: string[]): DiffLine[] => {
    const m = oldLines.length;
    const n = newLines.length;

    const dp: number[][] = Array.from({ length: m + 1 }, () =>
        Array(n + 1).fill(0)
    );
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (oldLines[i - 1] === newLines[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }

    const result: DiffLine[] = [];
    let i = m;
    let j = n;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
            result.push({
                type: 'same',
                oldNum: i,
                newNum: j,
                text: oldLines[i - 1],
            });
            i--;
            j--;
        } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            result.push({ type: 'added', newNum: j, text: newLines[j - 1] });
            j--;
        } else {
            result.push({ type: 'removed', oldNum: i, text: oldLines[i - 1] });
            i--;
        }
    }

    return result.reverse();
};

interface SchemaVersionDiffProps {
    versions: DocumentSchemaRecord[];
    onClose: () => void;
}

export const SchemaVersionDiff: React.FC<SchemaVersionDiffProps> = ({
    versions,
    onClose,
}) => {
    const sortedVersions = useMemo(
        () => [...versions].sort((a, b) => a.version - b.version),
        [versions]
    );

    const [leftVersion, setLeftVersion] = useState(
        sortedVersions.length >= 2
            ? sortedVersions[sortedVersions.length - 2].version
            : sortedVersions[0]?.version
    );
    const [rightVersion, setRightVersion] = useState(
        sortedVersions[sortedVersions.length - 1]?.version
    );

    const leftRecord = sortedVersions.find((v) => v.version === leftVersion);
    const rightRecord = sortedVersions.find((v) => v.version === rightVersion);

    const diff = useMemo(() => {
        if (!leftRecord || !rightRecord) return [];
        const oldLines = JSON.stringify(leftRecord.schema, null, 2).split('\n');
        const newLines = JSON.stringify(rightRecord.schema, null, 2).split(
            '\n'
        );
        return computeDiff(oldLines, newLines);
    }, [leftRecord, rightRecord]);

    const changedCount = diff.filter((l) => l.type !== 'same').length;

    return (
        <div className="space-y-3 max-w-[calc(100vw-400px)] overflow-hidden">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                    <ArrowLeftRight className="w-4 h-4" />
                    Version Comparison
                </h3>
                <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs rounded border border-input bg-background hover:bg-accent"
                >
                    Close Diff
                </button>
            </div>

            <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                    <label className="text-xs text-muted-foreground">
                        From:
                    </label>
                    <select
                        value={leftVersion}
                        onChange={(e) => setLeftVersion(Number(e.target.value))}
                        className="text-xs border rounded px-2 py-1 bg-background"
                    >
                        {sortedVersions.map((v) => (
                            <option key={v._id} value={v.version}>
                                v{v.version}
                            </option>
                        ))}
                    </select>
                </div>
                <ArrowLeftRight className="w-3 h-3 text-muted-foreground" />
                <div className="flex items-center gap-1.5">
                    <label className="text-xs text-muted-foreground">To:</label>
                    <select
                        value={rightVersion}
                        onChange={(e) =>
                            setRightVersion(Number(e.target.value))
                        }
                        className="text-xs border rounded px-2 py-1 bg-background"
                    >
                        {sortedVersions.map((v) => (
                            <option key={v._id} value={v.version}>
                                v{v.version}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {leftVersion === rightVersion ? (
                <div className="text-sm text-muted-foreground p-3 border rounded">
                    Select two different versions to compare.
                </div>
            ) : changedCount === 0 ? (
                <div className="text-sm text-muted-foreground p-3 border rounded">
                    No differences found between v{leftVersion} and v
                    {rightVersion}.
                </div>
            ) : (
                <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">
                        {changedCount} line
                        {changedCount !== 1 ? 's' : ''} changed
                    </div>
                    <div className="border rounded overflow-auto max-h-[75vh] max-w-[1200px] font-mono text-xs">
                        {diff.map((line, i) => (
                            <div
                                key={i}
                                className={`flex whitespace-pre ${
                                    line.type === 'added'
                                        ? 'bg-green-500/15 text-green-400'
                                        : line.type === 'removed'
                                        ? 'bg-red-500/15 text-red-400'
                                        : ''
                                }`}
                            >
                                <span className="select-none w-10 shrink-0 text-right pr-1 text-muted-foreground/50 border-r border-border/30">
                                    {line.oldNum ?? ''}
                                </span>
                                <span className="select-none w-10 shrink-0 text-right pr-1 text-muted-foreground/50 border-r border-border/30">
                                    {line.newNum ?? ''}
                                </span>
                                <span className="select-none w-5 shrink-0 text-center">
                                    {line.type === 'added'
                                        ? '+'
                                        : line.type === 'removed'
                                        ? '-'
                                        : ' '}
                                </span>
                                <span className="pr-2">{line.text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
